import React, { useState, useRef } from "react";
import { ReactComponent as MapIcon } from "../../../Assets/Icons/MapIcon.svg";
import { ReactComponent as MailIcon } from "../../../Assets/Icons/mailIcon.svg";
import { ReactComponent as WebIcon } from "../../../Assets/Icons/webIcon.svg";
import { ReactComponent as UserIcon } from "../../../Assets/Icons/userIcon.svg";
import { ReactComponent as PhoneIcon } from "../../../Assets/Icons/phoneIcon.svg";
import { ReactComponent as CategoryIcon } from "../../../Assets/Icons/categoryIcon.svg";
import { ReactComponent as CurrencyIcon } from "../../../Assets/Icons/currencyIcon.svg";
import { ReactComponent as DeleteIcon } from "../../../Assets/Icons/deleteIcon.svg";
import "../../common/OfferList/offerList.scss";
import { ReactComponent as ApprovedIcon } from "../../../Assets/Icons/approveIcon.svg";
import { ReactComponent as DeclinedIcon } from "../../../Assets/Icons/declinedIcon.svg";
import { ReactComponent as DownArrow } from "../../../Assets/Icons/blackDownArrow.svg";
import { ReactComponent as HourglassIcon } from "../../../Assets/Icons/hourglass.svg";
import { ReactComponent as OrganizationIcon } from "../../../Assets/Icons/Organization icon.svg";
import { ReactComponent as AbnIcon } from "../../../Assets/Icons/ABN icon.svg";
import { DatePicker, Dropdown, Tooltip } from "antd";
import toast from "../../../utils/toast";
import dayjs from "dayjs";
import TargetsTable from "./TargetsTable";
import PrimaryButton from "../../common/PrimaryButton";
import PrimaryOutlineButton from "../../common/PrimaryOutlineButton";
import CancelPopup from "../../common/CancelPopup";
import { approveOrRejectFundraiser, deleteFundraiserById } from "../../../services/fundraiserService";
import { useDispatch } from "react-redux";
import profileImagePlaceholderBlue from "../../../Assets/Images/PersonBlue.svg";
import { getS3PublicUrl } from "../../../utils/s3";

const PopupContent = ({
  merchantDetails,
  setSelectedYear,
  selectedYear,
  handleYearChange,
  onDelete,
  onDecline,
  onApprove,
  onStatusChange,
}) => {
  const dispatch = useDispatch();
  const [statusToggled, setStatusToggled] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);
  const statusMutationLockRef = useRef(false);

  if (!merchantDetails) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="loader"></div>
        <p className="mt-4 text-[#767676] font-poppins text-[15px]">Loading fundraiser details...</p>
      </div>
    );
  }

  const {
    address,
    contactPersonName,
    contactPersonNumber,
    logo,
    user,
    website,
    _id,
    status,
    description,
    reason,
    category,
    target,
    organizationName,
    abnNumber,
    accountEmail,
  } = merchantDetails;
  const subCategoryTitles = Array.isArray(merchantDetails?.subCategory)
    ? merchantDetails.subCategory
        .map((item) => String(item?.title || "").trim())
        .filter(Boolean)
        .join(", ")
    : "";

  const STATUS_BADGE_WIDTH = "126px";

  // Helper function to get status badge configuration - shows formatted status from API
  const getStatusBadgeConfig = (statusValue) => {
    const s = (statusValue || "").toLowerCase();
    
    // Format: capitalize first letter, replace underscores/spaces with proper format
    const formatStatus = (val) => {
      if (!val) return '';
      return val.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    };
    
    // Map to colors/icons but show formatted status label
    const statusMap = {
      'pending': { label: formatStatus(statusValue), bgColor: '#FFF1D6', borderColor: '#FFC961', iconColor: '#FFC961', width: STATUS_BADGE_WIDTH },
      'in-progress': { label: formatStatus(statusValue), bgColor: '#FFF1D6', borderColor: '#FFC961', iconColor: '#FFC961', width: STATUS_BADGE_WIDTH },
      'in progress': { label: formatStatus(statusValue), bgColor: '#FFF1D6', borderColor: '#FFC961', iconColor: '#FFC961', width: STATUS_BADGE_WIDTH },
      'in_process': { label: formatStatus(statusValue), bgColor: '#FFF1D6', borderColor: '#FFC961', iconColor: '#FFC961', width: STATUS_BADGE_WIDTH },
      'in_progress': { label: formatStatus(statusValue), bgColor: '#FFF1D6', borderColor: '#FFC961', iconColor: '#FFC961', width: STATUS_BADGE_WIDTH },
      'approved': { label: 'Live', bgColor: 'rgba(80, 205, 137, 0.15)', borderColor: '#50CD89', iconColor: '#50CD89', width: STATUS_BADGE_WIDTH },
      'live': { label: 'Live', bgColor: 'rgba(80, 205, 137, 0.15)', borderColor: '#50CD89', iconColor: '#50CD89', width: STATUS_BADGE_WIDTH },
      'declined': { label: formatStatus(statusValue), bgColor: 'rgba(236, 130, 110, 0.15)', borderColor: '#EC826E', iconColor: '#EC826E', width: STATUS_BADGE_WIDTH },
      'rejected': { label: formatStatus(statusValue), bgColor: 'rgba(236, 130, 110, 0.15)', borderColor: '#EC826E', iconColor: '#EC826E', width: STATUS_BADGE_WIDTH },
      'disabled': { label: formatStatus(statusValue), bgColor: 'rgba(236, 130, 110, 0.15)', borderColor: '#EC826E', iconColor: '#EC826E', width: STATUS_BADGE_WIDTH },
      'deleted': { label: formatStatus(statusValue), bgColor: 'rgba(236, 130, 110, 0.15)', borderColor: '#EC826E', iconColor: '#EC826E', width: STATUS_BADGE_WIDTH },
    };

    return statusMap[s] || { label: formatStatus(statusValue), bgColor: 'transparent', borderColor: '#FFC961', iconColor: '#FFC961', width: STATUS_BADGE_WIDTH };
  };

  // Helper function to render status icon (matches the main list)
  const renderStatusIcon = (statusValue, size = 12) => {
    const s = (statusValue || "").toLowerCase();
    
    if (s === 'approved' || s === 'live') {
      return (
        <svg width={size} height={size} viewBox="8 6 14 14" fill="none" style={{ display: "block" }}>
          <path d="M12 13.6667L14.3333 16L19 11" stroke="white" strokeWidth="2.0" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    } else if (s === 'declined' || s === 'rejected' || s === 'disabled') {
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" style={{ display: "block" }}>
          <path d="M7 12h10" />
        </svg>
      );
    } else if (s === 'deleted') {
      // White cross icon for deleted status
      return (
        <svg width={size - 2} height={size - 2} viewBox="0 0 14 14" fill="none">
          <path d="M11 3L3 11" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M3 3L11 11" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    } else if (s === 'pending') {
      return (
        <svg width={size + 2} height={size + 2} viewBox="0 0 16 16" fill="none" style={{ display: "block" }}>
          <circle cx="8" cy="8" r="8" fill="#FFC961" />
          <path d="M6.3313 6.0321C6.33139 5.81179 6.38384 5.59472 6.48431 5.39875C6.58478 5.20279 6.73043 5.03352 6.90928 4.90502C7.08813 4.77652 7.29508 4.69236 7.51289 4.65955C7.73069 4.62673 7.9531 4.6462 8.16186 4.71632C8.37061 4.78645 8.55956 4.90521 8.71334 5.06277C8.86713 5.22034 8.98124 5.41227 9.04616 5.6227C9.11108 5.83314 9.12503 6.05583 9.08688 6.27272C9.04874 6.48961 8.95959 6.69411 8.82689 6.86964L8.78282 6.91777C8.76127 6.94091 8.68672 7.01248 8.66255 7.03277C8.62515 7.06429 8.58608 7.09377 8.54548 7.12095L8.04248 7.4673C7.91817 7.55296 7.81658 7.66758 7.74638 7.80128C7.67618 7.93499 7.63949 8.08369 7.63964 8.23496" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="7.63989" cy="10.5802" r="0.7" fill="white"/>
        </svg>
      );
    } else if (['in-progress', 'in progress', 'in_process', 'in_progress'].includes(s)) {
      return <HourglassIcon width={size} height={size + 1} style={{ display: "block" }} />;
    } else {
      return (
        <svg width={size + 2} height={size + 2} viewBox="0 0 16 16" fill="none" style={{ display: "block" }}>
          <circle cx="8" cy="8" r="8" fill="#FFC961" />
          <path d="M6.3313 6.0321C6.33139 5.81179 6.38384 5.59472 6.48431 5.39875C6.58478 5.20279 6.73043 5.03352 6.90928 4.90502C7.08813 4.77652 7.29508 4.69236 7.51289 4.65955C7.73069 4.62673 7.9531 4.6462 8.16186 4.71632C8.37061 4.78645 8.55956 4.90521 8.71334 5.06277C8.86713 5.22034 8.98124 5.41227 9.04616 5.6227C9.11108 5.83314 9.12503 6.05583 9.08688 6.27272C9.04874 6.48961 8.95959 6.69411 8.82689 6.86964L8.78282 6.91777C8.76127 6.94091 8.68672 7.01248 8.66255 7.03277C8.62515 7.06429 8.58608 7.09377 8.54548 7.12095L8.04248 7.4673C7.91817 7.55296 7.81658 7.66758 7.74638 7.80128C7.67618 7.93499 7.63949 8.08369 7.63964 8.23496" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="7.63989" cy="10.5802" r="0.7" fill="white"/>
        </svg>
      );
    }
  };

  const getStatusConfirmationCopy = (statusKey) => {
    const s = (statusKey || "").toLowerCase();

    if (s === "approved" || s === "live") {
      return {
        heading: "Change Status",
        content: "Are you sure you want to make this fundraiser Live?",
      };
    }

    if (s === "pending") {
      return {
        heading: "Change Status",
        content: "Are you sure you want to move this fundraiser back to In-Progress?",
      };
    }

    if (s === "disabled") {
      return {
        heading: "Change Status",
        content: "Are you sure you want to disable this fundraiser?",
      };
    }

    if (s === "rejected" || s === "declined") {
      return {
        heading: "Change Status",
        content: "Are you sure you want to reject this fundraiser?",
      };
    }

    if (s === "deleted") {
      return {
        heading: "Delete Fundraiser",
        content: "Are you sure you want to delete this fundraiser? This action cannot be undone.",
      };
    }

    return {
      heading: "Change Status",
      content: "Are you sure you want to change this fundraiser status?",
    };
  };

  const handleBulkApprove = async (fundraiserId) => {
    if (statusChangeLoading || statusMutationLockRef.current) return false;
    if (typeof onApprove === 'function') {
      return onApprove(fundraiserId);
    } else {
      try {
        statusMutationLockRef.current = true;
        setStatusChangeLoading(true);
        const payload = { ids: [fundraiserId], status: "live" };
        const res = await dispatch(approveOrRejectFundraiser(payload))?.unwrap();
        if (res?.success === true) {
          setStatusToggled(!statusToggled);
          if (typeof onStatusChange === 'function') {
            onStatusChange("approved");
          }
          try { toast.success("Fundraiser approved successfully"); } catch (_) { }
          return true;
        }
      } catch (_) { }
      finally {
        setStatusChangeLoading(false);
        statusMutationLockRef.current = false;
      }
    }
    return false;
  };

  const handleBulkDecline = async (fundraiserId) => {
    if (statusChangeLoading || statusMutationLockRef.current) return;
    if (typeof onDecline === 'function') {
      return onDecline(fundraiserId);
    }
  };

  const openStatusConfirm = (nextStatus) => {
    if (statusChangeLoading || statusMutationLockRef.current) return;
    setPendingStatusChange(nextStatus);
  };

  const closeStatusConfirm = () => {
    if (statusChangeLoading) return;
    setPendingStatusChange(null);
  };

  const confirmStatusChange = async () => {
    if (!pendingStatusChange || statusChangeLoading || statusMutationLockRef.current) return;

    const nextStatus = pendingStatusChange.toLowerCase();
    statusMutationLockRef.current = true;
    setStatusChangeLoading(true);

    try {
      if (nextStatus === "approved" || nextStatus === "live") {
        const success =
          typeof onApprove === "function"
            ? await onApprove(_id)
            : await handleBulkApprove(_id);
        if (success) {
          setPendingStatusChange(null);
        }
        return;
      }

      if (nextStatus === "pending") {
        const payload = { ids: [_id], status: "pending" };
        const res = await dispatch(approveOrRejectFundraiser(payload))?.unwrap();
        if (res?.success === true) {
          setStatusToggled(!statusToggled);
          if (typeof onStatusChange === 'function') {
            onStatusChange("pending");
          }
          try { toast.success("Fundraiser moved to pending"); } catch (_) { }
          setPendingStatusChange(null);
        }
        return;
      }

      if (nextStatus === "disabled") {
        const payload = { ids: [_id], status: "disabled" };
        const res = await dispatch(approveOrRejectFundraiser(payload))?.unwrap();
        if (res?.success === true) {
          setStatusToggled(!statusToggled);
          if (typeof onStatusChange === 'function') {
            onStatusChange("disabled");
          }
          try { toast.success("Fundraiser disabled successfully"); } catch (_) { }
          setPendingStatusChange(null);
        }
        return;
      }

      if (nextStatus === "rejected" || nextStatus === "declined") {
        const success =
          typeof onDecline === "function"
            ? await onDecline(_id)
            : await handleBulkDecline(_id);
        if (success !== false) {
          setPendingStatusChange(null);
        }
        return;
      }

      if (nextStatus === "deleted") {
        if (typeof onDelete === 'function') {
          onDelete(_id);
        }
        setPendingStatusChange(null);
      }
    } catch (_) {
      // Keep the confirmation modal open so the user can retry.
    } finally {
      setStatusChangeLoading(false);
      statusMutationLockRef.current = false;
    }
  };

  const handleReviewStatus = async (fundraiserId, newStatus) => {
    openStatusConfirm(newStatus);
  };

  return (
    <div className="max-h-[85vh] w-full md:w-auto min-w-[300px] flex flex-col font-poppins">
      <div style={{ scrollbarWidth: "none" }} className="flex-1 overflow-y-auto px-1">
        <div className="w-full flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4 sm:gap-6">
          <div className="flex justify-start items-center gap-4 flex-1 min-w-0 w-full sm:w-auto">
            {(() => {
              const raw = logo
                || merchantDetails?.logoUrl
                || merchantDetails?.profileImage
                || merchantDetails?.fundraiserLogo
                || merchantDetails?.user?.logo
                || merchantDetails?.user?.profileImage;
              if (raw && typeof raw === "string" && raw.trim() !== "") {
                const src = getS3PublicUrl(raw);
                return (
                  <img
                    src={src}
                    alt="Fundraiser logo"
                    className="w-[93px] h-[93px] object-cover rounded-full"
                    onError={(e) => { e.target.src = profileImagePlaceholderBlue; }}
                  />
                );
              }
              return (
                <div className="w-[93px] h-[93px] rounded-full flex items-center justify-center bg-[#E6F7FF] border border-[#00ADEF33]">
                  <img
                    src={profileImagePlaceholderBlue}
                    alt="Default fundraiser avatar"
                    className="w-[70px] h-[70px] object-contain"
                  />
                </div>
              );
            })()}
            <div className="flex items-center gap-2 max-w-full">
              <h1 className="font-[600] text-[20px] md:text-[22px] lg:text-[24px] truncate max-w-[200px] sm:max-w-[250px] md:max-w-[350px]" title={user?.name}>
                {user?.name}
              </h1>
              {typeof onDelete === "function" && !['deleted', 'in-progress', 'in progress', 'in_process', 'in_progress', 'in-process'].includes(String(status || "").toLowerCase()) && (
                <Tooltip
                  title="Delete Fundraiser"
                  color="#00ADEF"
                  placement="top"
                  getPopupContainer={() => document.body}
                  overlayStyle={{ zIndex: 100000 }}
                >
                  <button
                    type="button"
                    onClick={() => onDelete(_id)}
                    className="p-1.5 ml-1 rounded-full hover:bg-[#fff1f0] transition-colors focus:outline-none flex items-center justify-center group"
                  >
                    <DeleteIcon className="w-4 h-4 text-[#ff4d4f] group-hover:scale-110 transition-transform" />
                  </button>
                </Tooltip>
              )}
            </div>
          </div>
          <div className="flex justify-between w-full sm:w-auto items-center gap-4 flex-shrink-0 mt-2 sm:mt-0">
            <div className="flex items-center gap-3 flex-shrink-0">
              {(() => {
                const statusLower = (status || "").toLowerCase();
                const isDeleted = statusLower === 'deleted';
                const isInProgress = ['in-progress', 'in progress', 'in_process', 'in_progress', 'in-process'].includes(statusLower);
                const isLive = statusLower === 'live' || statusLower === 'approved';
                const isRejected = statusLower === 'rejected' || statusLower === 'declined';
                const isPending = statusLower === 'pending';
                const isDisabled = statusLower === 'disabled';
                const isStatusDropdownDisabled = isDeleted || isInProgress || statusChangeLoading || statusMutationLockRef.current;
                
                return (
                  <Tooltip
                    title={statusDropdownOpen ? null : getStatusBadgeConfig(status).label}
                    placement="top"
                    getPopupContainer={() => document.body}
                    overlayStyle={{ zIndex: 10050 }}
                  >
                    <span className="inline-flex">
                  <Dropdown
                    menu={{
                      items: [
                        // Live status shows: Disabled
                        isLive && {
                          key: "disabled",
                          label: (
                            <div className="flex items-center gap-2 pr-6 pl-1 py-1">
                              <div className="flex items-center justify-center w-[16px] h-[16px] rounded-full bg-[#EC826E]">
                                {renderStatusIcon('disabled', 10)}
                              </div>
                              <span className="text-[13px] font-[400] font-poppins text-[#303033]">Disable</span>
                            </div>
                          ),
                          onClick: () => handleReviewStatus(_id, "disabled")
                        },
                        // Rejected status shows: Live, Disabled
                        isRejected && {
                          key: "approved",
                          label: (
                            <div className="flex items-center gap-2 pr-6 pl-1 py-1">
                              <div className="flex items-center justify-center w-[16px] h-[16px] rounded-full bg-[#50CD89]">
                                {renderStatusIcon('approved', 10)}
                              </div>
                              <span className="text-[13px] font-[400] font-poppins text-[#303033]">Live</span>
                            </div>
                          ),
                          onClick: () => handleReviewStatus(_id, "approved")
                        },
                        isRejected && {
                          key: "disabled",
                          label: (
                            <div className="flex items-center gap-2 pr-6 pl-1 py-1">
                              <div className="flex items-center justify-center w-[16px] h-[16px] rounded-full bg-[#EC826E]">
                                {renderStatusIcon('disabled', 10)}
                              </div>
                              <span className="text-[13px] font-[400] font-poppins text-[#303033]">Disable</span>
                            </div>
                          ),
                          onClick: () => handleReviewStatus(_id, "disabled")
                        },
                        // Pending status shows: Live, Rejected
                        isPending && {
                          key: "approved",
                          label: (
                            <div className="flex items-center gap-2 pr-6 pl-1 py-1">
                              <div className="flex items-center justify-center w-[16px] h-[16px] rounded-full bg-[#50CD89]">
                                {renderStatusIcon('approved', 10)}
                              </div>
                              <span className="text-[13px] font-[400] font-poppins text-[#303033]">Live</span>
                            </div>
                          ),
                          onClick: () => handleReviewStatus(_id, "approved")
                        },
                        isPending && {
                          key: "rejected",
                          label: (
                            <div className="flex items-center gap-2 pr-6 pl-1 py-1">
                              <div className="flex items-center justify-center w-[16px] h-[16px] rounded-full bg-[#EC826E]">
                                {renderStatusIcon('rejected', 10)}
                              </div>
                              <span className="text-[13px] font-[400] font-poppins text-[#303033]">Reject</span>
                            </div>
                          ),
                          onClick: () => handleReviewStatus(_id, "rejected")
                        },
                        // Disabled status shows: Live
                        isDisabled && {
                          key: "approved",
                          label: (
                            <div className="flex items-center gap-2 pr-6 pl-1 py-1">
                              <div className="flex items-center justify-center w-[16px] h-[16px] rounded-full bg-[#50CD89]">
                                {renderStatusIcon('approved', 10)}
                              </div>
                              <span className="text-[13px] font-[400] font-poppins text-[#303033]">Live</span>
                            </div>
                          ),
                          onClick: () => handleReviewStatus(_id, "approved")
                        }
                        // Deleted status shows nothing
                      ].filter(Boolean)
                    }}
                    trigger={["click"]}
                    placement="bottomLeft"
                    disabled={isStatusDropdownDisabled}
                    getPopupContainer={() => document.body}
                    overlayStyle={{ zIndex: 100000 }}
                    open={statusDropdownOpen}
                    onOpenChange={(nextOpen) => setStatusDropdownOpen(nextOpen)}
                  >
                {(() => {
                  const config = getStatusBadgeConfig(status);
                  
                  return (
                    <div
                      className="flex items-center h-[26px] pl-2.5 pr-2 rounded-full border-[0.5px] select-none"
                      style={{
                        backgroundColor: config.bgColor,
                        borderColor: config.borderColor,
                        width: config.width,
                        cursor: isStatusDropdownDisabled ? "not-allowed" : "pointer"
                      }}
                    >
                      <div
                        className="flex items-center justify-center w-[16px] h-[16px] rounded-full flex-shrink-0"
                        style={{
                          backgroundColor: (() => {
                            const s = (status || "").toLowerCase();
                            if (s === 'approved' || s === 'live') return "#50CD89";
                            if (s === 'declined' || s === 'rejected' || s === 'disabled' || s === 'deleted') return "#EC826E";
                            return "transparent"; // Remove yellow circle for pending/in-progress
                          })()
                        }}
                      >
                        {renderStatusIcon(status, 12)}
                      </div>
                      <span className="text-[12px] font-[400] ml-2 font-poppins text-[#303033] truncate flex-1 text-left">
                        {config.label}
                      </span>
                      {!isDeleted && !['in-progress', 'in progress', 'in_process', 'in_progress'].includes((status || "").toLowerCase()) && (
                        <div className="ml-2 flex items-center flex-shrink-0">
                          <svg width="7" height="4" viewBox="0 0 10 6" fill="none">
                            <path d="M1 1.5L5 5.5L9 1.5" stroke="#303033" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                      )}
                    </div>
                  );
                })()}
                    </Dropdown>
                    </span>
                  </Tooltip>
                );
              })()}
            </div>
          </div>
        </div>
        <div className="flex mt-4 justify-start items-center gap-2 text-[14px] font-[400] text-[#767676]">
          <CategoryIcon />
          <p className="mb-0 leading-none">{category && category[0]?.title}</p>
        </div>
        {subCategoryTitles ? (
          <div className="flex mt-4 justify-start items-center gap-2 text-[14px] font-[400] text-[#767676]">
            <CategoryIcon />
            <p className="mb-0 leading-none">
              <span className="font-[500] text-[#303033]">Subcategory: </span>
              {subCategoryTitles}
            </p>
          </div>
        ) : null}
        <div className="flex mt-4 justify-start items-center gap-2 text-[14px] font-[400] text-[#767676]">
          <MapIcon />
          <h3>{address}</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mt-2">
          {organizationName && (
            <div className="flex justify-start items-center gap-2 mt-1">
              {/* Outlined building/organisation icon - inline to guarantee no fill */}
              <svg width="20" height="21" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                <path d="M3 21H21" stroke="#00ADEF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M4.5 3H19.5V21H4.5V3Z" stroke="#00ADEF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 7H10.5M9 11H10.5M9 15H10.5" stroke="#00ADEF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M13.5 7H15M13.5 11H15M13.5 15H15" stroke="#00ADEF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <p className="text-[14px] font-[400] text-[#767676] truncate" title={organizationName}>
                <span className="font-[500] text-[#303033]">Organisation: </span>
                {organizationName}
              </p>
            </div>
          )}
          {abnNumber && (
            <div className="flex justify-start items-center gap-2 mt-1">
              {/* Outlined ID card / ABN icon - inline to guarantee no fill */}
              <svg width="24" height="21" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
                <rect x="1.5" y="3.5" width="21" height="17" rx="2.5" stroke="#00ADEF" strokeWidth="1.5"/>
                <circle cx="8" cy="10.5" r="2.25" stroke="#00ADEF" strokeWidth="1.5"/>
                <path d="M4.5 17C5.12 14.8 6.5 13.75 8 13.75C9.5 13.75 10.88 14.8 11.5 17" stroke="#00ADEF" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M14 9.5H19" stroke="#00ADEF" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M14 12.5H19" stroke="#00ADEF" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M14 15.5H17" stroke="#00ADEF" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <p className="text-[14px] font-[400] text-[#767676] truncate" title={abnNumber}>
                <span className="font-[500] text-[#303033]">ABN: </span>
                {abnNumber}
              </p>
            </div>
          )}
          <div className="flex justify-start items-center gap-2 mt-1">
            <MailIcon />
            <p className="text-[14px] font-[400] text-[#767676] truncate" title={user?.email}>
              <span className="font-[500] text-[#303033]">Fundraiser Email: </span>
              {user?.email}
            </p>
          </div>
          {accountEmail && (
            <div className="flex justify-start items-center gap-2 mt-1">
              <MailIcon />
              <p className="text-[14px] font-[400] text-[#767676] truncate" title={accountEmail}>
                <span className="font-[500] text-[#303033]">Organisation Email: </span>
                {accountEmail}
              </p>
            </div>
          )}
          <div className="flex justify-start items-center gap-3 mt-1">
            <WebIcon />
            <p className="text-[14px] font-[400] text-[#767676] text-start truncate" title={website}>
              <span className="font-[500] text-[#303033]">Website: </span>
              {website}
            </p>
          </div>
          <div className="flex justify-start items-center gap-2 mt-1 text-[14px] font-[400] text-[#767676]">
            <UserIcon />
            <p className="truncate" title={contactPersonName}>
              <span className="font-[500] text-[#303033]">Contact Name: </span>
              {contactPersonName}
            </p>
          </div>
          <div className="flex justify-start items-center gap-2 mt-1 text-[14px] font-[400] text-[#767676]">
            <PhoneIcon />
            <p className="truncate" title={contactPersonNumber}>
              <span className="font-[500] text-[#303033]">Contact Phone: </span>
              {contactPersonNumber}
            </p>
          </div>
        </div>

        <div className="w-full flex flex-col mt-4 gap-1 items-start font-poppins">
          <h2 className="text-[16px] font-[500] text-[#000000] text-start">Description:</h2>
          <p className="w-full text-start text-[14px] font-[400] text-[#767676]">
            {description || merchantDetails?.aboutUs || "No description available"}
          </p>
        </div>
        {reason?.length > 0 && (
          <div className="flex flex-col gap-1 mt-3">
            <h1 className="text-[16px] font-[500] text-start">Reasons:</h1>
            <ul className="list-disc pl-5 mb-5 text-[#767676] grid grid-cols-1 justify-items-start gap-4">
              {reason?.map((r, index) => (
                <li key={index} className="text-[14px] text-start">{r?.reason}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="pt-2 bg-white border-t mt-auto">
        {target?.targets?.length > 0 ? (
          <div className="w-full flex flex-col gap-4 items-start font-poppins p-2">
            <div className="w-full flex justify-between items-center gap-4 flex-wrap">
              <div className="flex justify-start items-center gap-4">
                <div className="flex justify-start items-start gap-4">
                  <CurrencyIcon />
                  <div className="flex flex-col gap-1">
                    <h2 className="text-[16px] font-[500] text-[#000000] text-start">Total Target:</h2>
                    <p className="w-full text-start text-[14px] font-[400] text-[#767676]">{`$${target?.totalTargetAmount}`}</p>
                  </div>
                </div>
                <div className="flex justify-start items-start gap-4 ml-4">
                  <CurrencyIcon />
                  <div className="flex flex-col gap-1">
                    <h2 className="text-[16px] font-[500] text-[#000000] text-start">Total Achieved:</h2>
                    <p className="w-full text-start text-[14px] font-[400] text-[#767676]">{`$${target?.totalAchievedAmount}`}</p>
                  </div>
                </div>
              </div>
              <DatePicker
                picker="year"
                style={{
                  width: "80px",
                  border: "2px solid #00ADEF",
                  borderRadius: "8px",
                  padding: "4px 10px",
                  cursor: "pointer",
                  color: "#000000",
                  fontWeight: "500",
                  backgroundColor: "white",
                }}
                suffixIcon={<DownArrow />}
                value={dayjs(selectedYear || "2025")}
                format="YYYY"
                allowClear={false}
                onChange={(date, dateString) => {
                  if (dateString) {
                    setSelectedYear(dateString);
                    handleYearChange(_id, dateString);
                  }
                }}
                inputReadOnly={true}
              />
            </div>
            <div className="w-full max-h-[150px] overflow-y-auto">
              <TargetsTable fundraiserDetails={merchantDetails} />
            </div>
          </div>
        ) : null}

        {(() => {
          const s = (status || "").toLowerCase();
          const isInProgress = ['in-progress', 'in progress', 'in_process', 'in_progress'].includes(s);
          
          return !isInProgress ? (
            <div className="w-full flex flex-col justify-center items-center gap-2 py-4">
              <CurrencyIcon className="!h-[52px] !w-[52px]" />
              <h2 className="text-[16px] font-[500] text-[#000000] text-center">No Fund Target has been set yet!</h2>
            </div>
          ) : null;
        })()}

        {(() => {
          const s = (status || "").toLowerCase();
          const isInProgress = ['in-progress', 'in progress', 'in_process', 'in_progress'].includes(s);
          const isPending = s === "pending";
          
          if (!isPending || isInProgress) return null;

          return (
            <div className="w-full flex justify-center items-center gap-4 mt-2 p-2">
              <PrimaryOutlineButton
                onClick={() => openStatusConfirm("rejected")}
                label="Reject"
                className="h-[44px] w-[120px] rounded-[5px]"
              />
              <PrimaryButton
                label="Live"
                onClick={() => openStatusConfirm("approved")}
                className="h-[44px] w-[120px] rounded-[5px]"
              />
            </div>
          );
        })()}
      </div>
      {pendingStatusChange && (
        <CancelPopup
          handleNo={closeStatusConfirm}
          handleYes={confirmStatusChange}
          heading={getStatusConfirmationCopy(pendingStatusChange).heading}
          content={getStatusConfirmationCopy(pendingStatusChange).content}
          confirmation={pendingStatusChange === "approved" || pendingStatusChange === "live"}
          popupClassName="w-[335px] sm:w-[470px] h-[255px] sm:h-[245px]"
          confirmDisabled={statusChangeLoading}
          cancelDisabled={statusChangeLoading}
          confirmLabel={statusChangeLoading ? "Updating..." : "Confirm"}
        />
      )}
    </div>
  );
};

export default PopupContent;
